/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, message, Typography, Tabs } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { UserLoggedIn } from '~components/popups-modals/CachedSharesModals/TenderClaimModals/UserLoggedIn';
import { CreateUserClaim } from '~components/popups-modals/CachedSharesModals/TenderClaimModals/CreateUserClaim';
import { UserLogin } from '~components/popups-modals/CachedSharesModals/TenderClaimModals/UserLogin';
import { useQuery } from 'react-query';
import { getCurrentUser, validateUserToken } from '~src/services/api/user';
import ForgotPasswordForm from '~components/organisms/ForgotPasswordForm/ForgotPasswordForm';

interface TenderOptionsModalProps extends Partial<ModalProps> {
  onDone?: () => void;
  onCancel?: () => void;
  shareData?: { shareKey?: string | null; shareId?: string } | null;
}

type AuthTab =
  | 'user-logged-in'
  | 'user-login'
  | 'create-new-user'
  | 'forgot-password';

export const TenderClaimModal: React.FC<TenderOptionsModalProps> = ({
  onDone,
  onCancel,
  shareData,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [active, setActive] = useState<AuthTab>('user-login');
  const switchTab = useCallback((tab: AuthTab) => setActive(tab), []);

  // VALIDATE USER TOKEN | do nothing right now
  // const {
  //   data: isUserInAccount,
  //   isLoading: isUserInAccountLoading,
  // } = useQuery(['validate-user-token'], () => validateUserToken());
  // console.log(`isUserLoggedIn`, Boolean(isUserInAccount));

  // CURRENT USER DATA
  const { data: currentUser, isLoading } = useQuery(
    ['current-user'],
    getCurrentUser,
    {
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: (count, err: any) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) return false;
        return count < 2;
      },
    }
  );

  const isLoggedIn = !!(
    currentUser &&
    (currentUser.email || (currentUser as any).id)
  );

  useEffect(() => {
    if (isLoggedIn) setActive('user-logged-in');
  }, [isLoggedIn]);

  const handleAfterClose = useCallback(() => {
    setActive(isLoggedIn ? 'user-logged-in' : 'user-login');
  }, [isLoggedIn]);

  const renderOptions = () => {
    switch (active) {
      case 'user-logged-in':
        return (
          <UserLoggedIn
            switchTab={switchTab}
            currentUserData={currentUser}
            shareId={shareData?.shareId}
            shareKey={shareData?.shareKey}
          />
        );
      case 'user-login':
        return (
          <UserLogin
            switchTab={switchTab}
            shareId={shareData?.shareId}
            shareKey={shareData?.shareKey}
          />
        );
      case 'create-new-user':
        return (
          <CreateUserClaim
            switchTab={switchTab}
            shareId={shareData?.shareId}
            shareKey={shareData?.shareKey}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPasswordForm switchTab={switchTab} isOnLoginPage={false} />
        );
      default:
        return (
          <UserLogin
            switchTab={switchTab}
            shareId={shareData?.shareId}
            shareKey={shareData?.shareKey}
          />
        );
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'tender-claim' })}
          icon={['far', 'check-circle']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={633}
      centered
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      afterClose={handleAfterClose}
      {...modalProps}>
      <div>{renderOptions()}</div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});

export default TenderClaimModal;
